import { Component, useMemo, useState, type ErrorInfo, type ReactNode } from 'react';
import {
  AlertTriangle, ArrowRight, BookOpen, Check, CheckCircle2,
  CircleHelp, Copy, Download, ExternalLink, FileText, Layers,
  Lock, Radio, RefreshCw, Search, Shield, Sparkles, X,
} from 'lucide-react';
import {
  The, DauThe, TheSoLieu, NhanTrangThai, ChuaCoDuLieu,
} from '../components/ui';
import type { ManHinh } from '../components/ThanhBen';

// ===== Static Product & Architecture Metadata =====
export const PRODUCT_METADATA = {
  manifestVersion: '0.8.10',
  minManifestVersion: '0.8.2',
  dashboardVersion: 'MVP (Final)',
  engineName: 'FBI Social Engine',
  architecture: 'Thin Client / Fat Server (ARCH-01)',
  productionUrl: 'https://fbmax.net',
  extensionDownloadUrl: '/api/downloads/extension',
} as const;

// ===== Types for Navigation & Categorization =====
export type HelpCategory = 'all' | 'start' | 'modes' | 'payment' | 'troubleshoot' | 'security' | 'faq';
export type HelpSectionAnchor =
  | 'checklist'
  | 'getting-started'
  | 'post-modes'
  | 'plans-payment'
  | 'troubleshooting'
  | 'privacy-security'
  | 'faq-support';

export interface HelpTopic {
  id: string;
  anchor: HelpSectionAnchor;
  category: HelpCategory;
  title: string;
  badge: string;
  badgeColor: 'xanh' | 'xam' | 'vang' | 'do';
  summary: string;
  details: string[];
  disclaimers?: string[];
  actionRoute?: ManHinh;
  actionText?: string;
  actionExternal?: string;
  actionSpecial?: 'upgrade';
}

export interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  actionRoute?: ManHinh;
  actionText?: string;
  actionExternal?: string;
}

// ===== Initial Static Topics Data =====
export const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'topic-install-companion',
    anchor: 'getting-started',
    category: 'start',
    title: 'Cài đặt Extension FBMax Companion v0.8.8',
    badge: 'Manifest V3',
    badgeColor: 'xanh',
    summary: 'Cài đặt tiện ích mở rộng vào Google Chrome để thực hiện kết nối phiên với Facebook an toàn.',
    details: [
      'FBMax hoạt động theo mô hình Thin Client / Fat Server (ARCH-01): Dashboard trên trình duyệt điều phối, còn Extension thực thi vận chuyển mạng trong phiên người dùng hợp lệ.',
      'Tải bản tiện ích nén ZIP chính thức từ nút tải trong Dashboard (phiên bản hiện hành: 0.8.8, yêu cầu tối thiểu: 0.8.2).',
      'Mở Chrome, truy cập "chrome://extensions", bật công tắc "Chế độ dành cho nhà phát triển" (Developer mode).',
      'Nhấp nút "Tải tiện ích đã giải nén" (Load unpacked) và chọn thư mục Extension vừa giải nén.',
      'Mở một tab Facebook (https://www.facebook.com) và giữ tab này luôn mở để duy trì phiên làm việc.',
    ],
    actionExternal: PRODUCT_METADATA.extensionDownloadUrl,
    actionText: 'Tải Extension 0.8.8',
  },
  {
    id: 'topic-identity-pairing',
    anchor: 'getting-started',
    category: 'start',
    title: 'Ghép nối danh tính & Xác minh Actor (IDENTITY-01)',
    badge: 'Bảo mật danh tính',
    badgeColor: 'xanh',
    summary: 'Phân định rõ ràng giữa tài khoản FBMax, tài khoản cá nhân Facebook và Fanpage thực thi.',
    details: [
      'Chủ phiên (c_user / mainUid): Tài khoản Facebook cá nhân gốc đang đăng nhập trong tab Chrome.',
      'Actor thực thi (i_user / UID hiện hành): Tư cách đang được chọn trên Dashboard để gửi bài (có thể là trang cá nhân hoặc Fanpage bạn đang quản trị).',
      'Khóa Actor tác vụ (fbUid): Khi bạn gửi tác vụ tạo bài hoặc bình luận, danh tính actor được chụp cố định tại thời điểm đó và không tự động thay đổi nếu sau này bạn chuyển trang trên Facebook.',
      'Ràng buộc Fanpage: Nếu Actor đang chọn là Page, hệ thống tự động khóa tính năng "Đăng ẩn danh" vì Facebook không hỗ trợ Page đăng ẩn danh trong nhóm.',
      'Quan sát thanh tiêu đề: Thẻ Actor ở góc trên luôn hiển thị rõ bạn đang ở tư cách Cá nhân hay Page.',
    ],
    actionRoute: 'tong-quan',
    actionText: 'Kiểm tra Actor tại Tổng quan',
  },
  {
    id: 'topic-proven-modes',
    anchor: 'post-modes',
    category: 'modes',
    title: 'Các chế độ đăng bài đã kiểm chứng & hỗ trợ chuẩn xác',
    badge: 'Đã kiểm chứng',
    badgeColor: 'xanh',
    summary: 'Danh sách các khuôn mẫu đăng bài vào nhóm đã được xác thực an toàn trên hệ thống production.',
    details: [
      '1. Đăng bài nhóm dạng văn bản thuần túy (POST_GROUP_TEXT): Hỗ trợ Spintax nội dung, gắn link.',
      '2. Đăng bài nhóm kèm 1 hình ảnh (POST_GROUP_PHOTO): Tải ảnh trực tiếp lên hạ tầng lưu trữ trước khi dispatch.',
      '3. Đăng bài nhóm kèm nhiều ảnh (POST_GROUP_PHOTO): Hỗ trợ album từ 1 đến 10 ảnh đã được kiểm chứng.',
      '4. Đăng bài nhóm kèm video (POST_GROUP_VIDEO): Tải lên video MP4 có giám sát tiến độ tải.',
      '5. Đăng bài nhóm phông nền màu (POST_GROUP_BACKGROUND): Tạo bài viết nổi bật dạng thẻ nền.',
      '6. Tự động bình luận đầu tiên (First Comment): Hàng đợi tự động gửi bình luận ngay sau khi bài đăng cha được xác nhận thành công.',
      '7. Lên lịch định kỳ tự động (SCHEDULE_RECURRING): Hẹn giờ đăng bài theo khung giờ định sẵn với cơ chế giãn cách ngẫu nhiên an toàn.',
    ],
    actionRoute: 'tao-bai',
    actionText: 'Đến màn Tạo bài',
  },
  {
    id: 'topic-durable-group-join',
    anchor: 'post-modes',
    category: 'modes',
    title: 'Tham gia nhóm tự động theo đợt an toàn (Durable Join Batch)',
    badge: 'Đợt an toàn',
    badgeColor: 'xanh',
    summary: 'Cơ chế xếp hàng tham gia nhóm có giãn cách ngẫu nhiên, chia hiệp nghỉ và kiểm soát hạn mức ngày.',
    details: [
      'Chính sách giãn cách an toàn: Giãn cách 3–5 phút ngẫu nhiên giữa các nhóm; tự động chia hiệp 3 nhóm và nghỉ 30 phút giữa các hiệp để bảo vệ tài khoản khỏi checkpoint.',
      'Kiểm soát hạn mức ngày: Hàng đợi máy chủ kiểm tra hạn mức tham gia tối đa trong ngày trước khi điều phối tác vụ (hiển thị theo hạn mức máy chủ trả về).',
      'Phản hồi tiếp nhận (Accepted): Khi gửi yêu cầu, máy chủ trả về trạng thái "Đã tiếp nhận" (Accepted) và cấp mã đợt để theo dõi tiến độ, lịch hẹn thực thi (nextEligibleAt tính theo đồng hồ máy chủ) chứ không ngụy tạo kết quả đã tham gia.',
      'Trạng thái từng nhóm: Hiển thị minh bạch các trạng thái thực tế gồm Đã tham gia (JOINED), Chờ duyệt (REQUESTED_PENDING), Cần trả lời câu hỏi khảo sát (NEEDS_USER_ACTION), Chưa xác định (UNKNOWN), Không khả dụng (UNAVAILABLE), Bị giới hạn tần suất (RATE_LIMITED), Checkpoint (CHECKPOINT), Hết hạn phiên (SESSION_EXPIRED), Đang chờ (QUEUED), Đang thực thi (RUNNING), Đã bỏ qua (SKIPPED), Đã hủy (CANCELLED).',
      'Điều khiển đợt tham gia: Cho phép tạm dừng, tiếp tục hoặc hủy đợt cho các nhóm còn đang chờ. Lưu ý nhóm đang thực thi trên Facebook không thể hủy giữa chừng; và tuyệt đối không có nút thử lại cho trạng thái Chưa xác định (UNKNOWN) theo quy tắc DATA-02.',
      'Chính sách Fanpage: Chức năng tham gia nhóm tự động hiện chỉ hỗ trợ tài khoản cá nhân, chưa hỗ trợ Fanpage (khóa theo chính sách hệ thống, không tuyên bố lỗi bất khả thi của nền tảng).',
      'Tìm kiếm nhóm: Thanh tìm kiếm chỉ tìm trong bộ nhớ tạm các nhóm đã đồng bộ (CACHE), không phải công cụ quét mở rộng toàn Facebook.',
    ],
    actionRoute: 'kenh-nhom',
    actionText: 'Đến màn Kênh & Nhóm',
  },
  {
    id: 'topic-disclaimed-modes',
    anchor: 'post-modes',
    category: 'modes',
    title: 'Ranh giới & Các tính năng CHƯA hỗ trợ / Không khẳng định',
    badge: 'Ranh giới kỹ thuật',
    badgeColor: 'vang',
    summary: 'Những tính năng chưa được xác thực nhằm bảo vệ an toàn tối đa cho tài khoản Facebook của bạn.',
    details: [
      'Chỉ các khuôn mẫu được xác thực cấu trúc mới được cấp phép thực thi nhằm tuân thủ Hiến pháp không cào DOM (ARCH-02).',
      'Mọi tác vụ đều đi qua hàng đợi máy chủ kiểm tra hạn mức và khóa trùng lặp trước khi gửi xuống Extension.',
    ],
    disclaimers: [
      'Không khẳng định hỗ trợ toàn bộ các loại phông nền Facebook (chỉ hỗ trợ các preset bảng màu đã được xác thực).',
      'Không khẳng định hỗ trợ đăng Reels lên trang cá nhân hoặc Page qua luồng này.',
      'Không hỗ trợ đăng chéo đồng thời (crossposting) giữa trang cá nhân và Page, hoặc đăng chéo kết hợp ẩn danh.',
      'Chưa hoàn tất nghiệm thu chế độ ẩn danh trong bản phát hành này (tạm khóa an toàn trên toàn hệ thống).',
      'Tính năng tìm kiếm nhóm trên Dashboard hiện tại chỉ tìm kiếm trong bộ nhớ tạm nhóm ĐÃ ĐỒNG BỘ của bạn (source=CACHE), không phải công cụ cào tìm toàn Facebook.',
    ],
    actionRoute: 'kenh-nhom',
    actionText: 'Xem danh sách nhóm đã đồng bộ',
  },
  {
    id: 'topic-plans-payment',
    anchor: 'plans-payment',
    category: 'payment',
    title: 'Gói cước, Hạn mức & Cơ chế thanh toán VietQR',
    badge: 'Minh bạch gói',
    badgeColor: 'xanh',
    summary: 'Hiển thị giao diện tĩnh để khách hàng tìm hiểu, kiểm soát quyền hạn nghiêm ngặt tại máy chủ.',
    details: [
      'Hiển thị tĩnh tiếp thị (UI-04): Bạn có thể xem trước tất cả các màn hình và tính năng trên hệ thống để đánh giá sự phù hợp. Tuy nhiên, quyền thực thi thực tế (gửi bài, tạo lịch, phân tích) được kiểm soát bởi máy chủ.',
      'Thanh toán chuyển khoản VietQR: Mỗi lượt đăng ký gói tạo ra một mã đơn duy nhất kèm cú pháp chuyển khoản cố định (được gắn khóa idempotency để tránh trùng lặp đơn).',
      'Cơ chế đối soát Webhook (Deferred): Hệ thống ghi nhận thanh toán qua quy trình đối soát ngân hàng theo đợt. Trạng thái đơn hàng sẽ chuyển từ PENDING sang ACTIVATED khi nhận được khớp lệnh.',
      'Cấp quyền thủ công (Manual Grant): Quản trị viên hệ thống có thẩm quyền kiểm tra chứng từ chuyển khoản và kích hoạt tài khoản thủ công trong trường hợp cần hỗ trợ khẩn cấp.',
      'Khi hết hạn mức: Hệ thống sẽ trả mã 402 PAYMENT_REQUIRED và hiển thị hộp thoại nâng cấp rõ ràng, không làm gián đoạn dữ liệu đã lưu.',
    ],
    actionSpecial: 'upgrade',
    actionText: 'Xem bảng giá & Gói cước',
  },
  {
    id: 'topic-troubleshooting-connection',
    anchor: 'troubleshooting',
    category: 'troubleshoot',
    title: 'Xử lý sự cố mất kết nối hoặc lệch phiên tab',
    badge: 'Khắc phục nhanh',
    badgeColor: 'vang',
    summary: 'Khắc phục nhanh khi đèn trạng thái Extension báo màu vàng hoặc không nhận diện được Actor.',
    details: [
      '1. Kiểm tra Extension: Mở "chrome://extensions" để đảm bảo FBMax Companion 0.8.8 vẫn đang được BẬT.',
      '2. Mở tab Facebook: Luôn giữ ít nhất một tab Facebook đang đăng nhập đúng tài khoản muốn thao tác.',
      '3. Tải lại Dashboard: Nhấn F5 hoặc nút "Làm mới" trên thanh công cụ để Dashboard nhận diện lại phiên kết nối.',
      '4. Lệch Actor: Nếu bạn vừa chuyển đổi sang Fanpage trên tab Facebook, hãy chọn lại tài khoản tương ứng trong menu chọn tài khoản ở đầu trang Dashboard để đồng bộ.',
    ],
    actionRoute: 'tong-quan',
    actionText: 'Đến trang Tổng quan',
  },
  {
    id: 'topic-troubleshooting-unknown',
    anchor: 'troubleshooting',
    category: 'troubleshoot',
    title: 'CẢNH BÁO: Tác vụ báo "Chưa xác định" (UNKNOWN_RESULT)',
    badge: 'Quy tắc an toàn',
    badgeColor: 'do',
    summary: 'TUYỆT ĐỐI KHÔNG bấm gửi lại liên tiếp khi tác vụ mang trạng thái Chưa xác định.',
    details: [
      'Luật DATA-02 quy định: Nếu quá thời gian phản hồi hoặc mạng gián đoạn, hệ thống không thể khẳng định bài đã đăng hay chưa nên bắt buộc ghi nhận trạng thái "Chưa xác định" (UNKNOWN_RESULT).',
      'LÝ DO KHÔNG ĐƯỢC RETRY NGAY: Facebook có thể ĐÃ TIẾP NHẬN và đang xử lý bài đăng của bạn. Việc bấm gửi lại ngay lập tức sẽ tạo ra bài viết trùng lặp hoặc khiến Facebook đánh dấu hành vi spam.',
      'Việc cần làm: Chờ 3-5 phút, vào nhóm kiểm tra thủ công xem bài đã xuất hiện chưa, hoặc theo dõi tiến trình đối soát tự động của hệ thống tại màn "Tiến Trình".',
      'Nếu sau 15 phút bài vẫn không xuất hiện, hãy kiểm tra xem nhóm có bật chế độ phê duyệt bài viết trước của quản trị viên hay không.',
    ],
    actionRoute: 'tien-trinh',
    actionText: 'Xem lịch sử Tiến Trình',
  },
  {
    id: 'topic-privacy-security',
    anchor: 'privacy-security',
    category: 'security',
    title: 'Bảo mật tài khoản & Không chia sẻ Token phiên (SEC-01 / SEC-02)',
    badge: 'Tuyệt mật',
    badgeColor: 'do',
    summary: 'Các nguyên tắc cốt lõi bảo vệ dữ liệu phiên và tài sản số của bạn.',
    details: [
      'Bảo mật phiên qua Extension: FBMax hoạt động dựa trên phiên đăng nhập Facebook sẵn có trên Chrome qua Companion Extension (ARCH-01/ARCH-02), không yêu cầu nhập thông tin mật khẩu Facebook trên Dashboard.',
      'Cách ly Tenant (SEC-01): Dữ liệu nhóm, chiến dịch, bài viết và lịch trình của bạn được phân vùng bảo mật riêng biệt theo Tenant, không thể bị truy cập bởi người dùng khác.',
      'Tuyệt đối không chia sẻ Token: Mã phiên đăng nhập (JWT), token MCP Agent, cookie hoặc chuỗi mã lỗi kỹ thuật có thể chứa thông tin nhạy cảm. Không bao giờ gửi chúng lên mạng xã hội hoặc ảnh chụp màn hình công khai.',
      'Khóa Token MCP: Khóa MCP sinh ra chỉ hiển thị MỘT LẦN DUY NHẤT. Nếu bị lộ, bạn hãy vào màn MCP Agent để thu hồi khóa ngay lập tức.',
    ],
    actionRoute: 'mcp-agent',
    actionText: 'Quản lý khóa MCP Agent',
  },
  {
    id: 'topic-faq-support',
    anchor: 'faq-support',
    category: 'faq',
    title: 'Cung cấp Job ID khi cần hỗ trợ kỹ thuật',
    badge: 'Hỗ trợ chuẩn',
    badgeColor: 'xanh',
    summary: 'Cách lấy mã tương quan (Job ID) để kỹ thuật viên tra cứu nhật ký audit nhanh nhất.',
    details: [
      'Mỗi tác vụ gửi lên hệ thống đều được cấp một Job ID (Mã tác vụ) duy nhất và mã tương quan (correlationId).',
      'Khi gặp sự cố cần giải đáp, bạn hãy vào màn "Tiến Trình", bấm vào tác vụ bị lỗi và sao chép Job ID để gửi cho đội ngũ hỗ trợ.',
      'Kỹ thuật viên sẽ tra cứu chính xác sự kiện trong nhật ký đối soát máy chủ mà không cần yêu cầu bạn cung cấp mật khẩu hay thông tin riêng tư.',
      'Kênh hỗ trợ chính thức: Hệ thống hỗ trợ trực tiếp qua kênh quản trị viên / workspace của hệ thống tại tên miền chính thức https://fbmax.net. Không có số hotline hay email tự phát không xác thực.',
    ],
    actionRoute: 'tien-trinh',
    actionText: 'Đến màn Tiến Trình',
  },
];

// ===== Quick-Start Checklist Items =====
export const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    id: 'step-install-ext',
    title: '1. Cài đặt Extension Companion 0.8.8',
    description: 'Tải bộ cài đặt tiện ích, giải nén và nạp vào Chrome qua chế độ Developer mode.',
    actionExternal: PRODUCT_METADATA.extensionDownloadUrl,
    actionText: 'Tải Extension',
  },
  {
    id: 'step-open-fb',
    title: '2. Đăng nhập Facebook trong tab Chrome',
    description: 'Mở trang Facebook cá nhân và giữ tab hoạt động để duy trì phiên kết nối.',
    actionExternal: 'https://www.facebook.com',
    actionText: 'Mở Facebook',
  },
  {
    id: 'step-verify-actor',
    title: '3. Xác minh Actor Facebook trên Header',
    description: 'Kiểm tra biểu tượng Actor ở đầu trang để biết đang thao tác dưới tư cách Cá nhân hay Page.',
    actionRoute: 'tong-quan',
    actionText: 'Đến Tổng quan',
  },
  {
    id: 'step-sync-groups',
    title: '4. Kiểm tra danh sách nhóm đã đồng bộ',
    description: 'Mở màn "Kênh & nhóm" để kiểm tra các nhóm mà Extension đã đọc từ tài khoản đang chọn.',
    actionRoute: 'kenh-nhom',
    actionText: 'Đến Kênh & nhóm',
  },
  {
    id: 'step-test-post',
    title: '5. Soạn bài đăng kiểm thử & theo dõi tiến trình',
    description: 'Tạo một bài viết văn bản hoặc kèm ảnh, gửi tác vụ và xem kết quả tại "Tiến Trình".',
    actionRoute: 'tao-bai',
    actionText: 'Đến Tạo bài',
  },
];

// ===== Pure Filter Function for Unit Testing & UI =====
export function filterHelpTopics(topics: HelpTopic[], query: string, category: HelpCategory): HelpTopic[] {
  const cleanQuery = (query || '').trim().toLowerCase();

  return topics.filter(topic => {
    if (category !== 'all' && topic.category !== category) {
      return false;
    }
    if (!cleanQuery) return true;

    const matchTitle = topic.title.toLowerCase().includes(cleanQuery);
    const matchSummary = topic.summary.toLowerCase().includes(cleanQuery);
    const matchBadge = topic.badge.toLowerCase().includes(cleanQuery);
    const matchDetails = topic.details.some(d => d.toLowerCase().includes(cleanQuery));
    const matchDisclaimers = topic.disclaimers ? topic.disclaimers.some(d => d.toLowerCase().includes(cleanQuery)) : false;

    return matchTitle || matchSummary || matchBadge || matchDetails || matchDisclaimers;
  });
}

// ===== Markdown Concise Guide Generator =====
export function generateMarkdownGuide(): string {
  return `# CẨM NANG HƯỚNG DẪN SỬ DỤNG FBMAX
Phiên bản Companion Extension: ${PRODUCT_METADATA.manifestVersion} (Yêu cầu tối thiểu: ${PRODUCT_METADATA.minManifestVersion})
Phiên bản Dashboard: ${PRODUCT_METADATA.dashboardVersion}
Cổng hệ thống: ${PRODUCT_METADATA.productionUrl}
Kiến trúc: ${PRODUCT_METADATA.architecture}

---

## 1. KHỞI ĐẦU NHANH
1. Cài đặt Extension FBMax Companion v${PRODUCT_METADATA.manifestVersion} vào Chrome (chrome://extensions -> Developer mode -> Load unpacked).
2. Mở tab Facebook (https://www.facebook.com) và đăng nhập tài khoản của bạn.
3. Mở Dashboard FBMax, kiểm tra chỉ báo radio màu xanh "Đang kết nối · Ext ${PRODUCT_METADATA.manifestVersion}".
4. Xác minh Actor đang hoạt động (Cá nhân hoặc Page) trên thanh tiêu đề (IDENTITY-01).
5. Đồng bộ và kiểm tra danh sách nhóm trong màn "Kênh & nhóm".

---

## 2. CÁC KHUÔN MẪU ĐĂNG BÀI ĐÃ XÁC THỰC
- Đăng bài nhóm văn bản (POST_GROUP_TEXT)
- Đăng bài nhóm kèm 1 ảnh (POST_GROUP_PHOTO)
- Đăng bài nhóm album nhiều ảnh 1-10 ảnh (POST_GROUP_PHOTO)
- Đăng bài nhóm kèm video (POST_GROUP_VIDEO)
- Đăng bài nhóm dạng phông nền màu (POST_GROUP_BACKGROUND)
- Tự động bình luận đầu tiên (First Comment outbox)
- Lên lịch đăng bài định kỳ tự động (SCHEDULE_RECURRING)

LƯU Ý GIỚI HẠN KỸ THUẬT:
- KHÔNG hỗ trợ toàn bộ các phông nền tùy biến Facebook.
- KHÔNG hỗ trợ đăng Reels lên Profile hay Fanpage.
- KHÔNG hỗ trợ đăng chéo Profile + Fanpage đồng thời hoặc kết hợp ẩn danh.
- Chưa hoàn tất nghiệm thu chế độ ẩn danh trong bản phát hành này.
- Tìm kiếm nhóm chỉ tìm trong nhóm đã đồng bộ (source=CACHE).

---

## 3. GÓI CƯỚC & THANH TOÁN
- Freemium Preview (UI-04): Bạn được xem trước tất cả màn hình tĩnh; quyền thực thi được kiểm soát tại máy chủ.
- Chuyển khoản VietQR: Mỗi đơn có mã chuyển khoản duy nhất; đối soát qua webhook có kiểm soát (deferred).
- Cấp quyền thủ công: Quản trị viên có thể kích hoạt gói khi có chứng từ giao dịch hợp lệ.

---

## 4. XỬ LÝ SỰ CỐ & QUY TẮC AN TOÀN
- Khi tác vụ mang trạng thái "Chưa xác định" (UNKNOWN_RESULT):
  TUYỆT ĐỐI KHÔNG GỬI LẠI NGAY LẬP TỨC. Facebook có thể đã tiếp nhận bài đăng. Gửi lại dồn dập sẽ gây spam hoặc khóa tính năng.
- Khi cần hỗ trợ kỹ thuật:
  Vào màn "Tiến Trình", sao chép chính xác Job ID (Mã tác vụ) hoặc correlationId để gửi hỗ trợ. Không chia sẻ mật khẩu, token hay cookie.

Kênh hỗ trợ chính thức: Hệ thống quản trị viên / workspace tại ${PRODUCT_METADATA.productionUrl}.
`;
}

// ===== Error Boundary Component (UI-03) =====
export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
}

export class HelpCenterErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const message = error instanceof Error ? error.message : 'Đã xảy ra lỗi hiển thị trung tâm trợ giúp.';
    return { hasError: true, errorMessage: message };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    console.error('HelpCenter error boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-rose-600 dark:text-rose-400" />
            <h2 className="text-base font-bold">Không thể hiển thị Trung tâm trợ giúp</h2>
          </div>
          <p className="mt-2 text-sm text-rose-700 dark:text-rose-400">
            {this.state.errorMessage || 'Đã có lỗi render xảy ra khi xử lý nội dung trợ giúp.'}
          </p>
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={() => this.setState({ hasError: false, errorMessage: null })}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-rose-700"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Thử tải lại vùng này
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ===== Props Interface =====
export interface HelpCenterProps {
  onDieuHuong: (man: ManHinh) => void;
  onMoNangCap: () => void;
  extConnected?: boolean;
  extVersion?: string | null;
  hasAccount?: boolean;
}

// ===== Main HelpCenter Component =====
export function HelpCenter({
  onDieuHuong,
  onMoNangCap,
  extConnected = false,
  extVersion = null,
  hasAccount = false,
}: HelpCenterProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState<HelpCategory>('all');
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('fbmax_help_checklist');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [copied, setCopied] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);

  // Toggle single checklist item and persist to localStorage
  const toggleChecklistItem = (id: string) => {
    setChecklist(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem('fbmax_help_checklist', JSON.stringify(next));
      } catch {
        // Local preference only; ignore storage failure
      }
      return next;
    });
  };

  const resetChecklist = () => {
    setChecklist({});
    try {
      localStorage.removeItem('fbmax_help_checklist');
    } catch {}
  };

  const completedCount = useMemo(() => {
    return CHECKLIST_ITEMS.filter(item => checklist[item.id]).length;
  }, [checklist]);

  // Filter topics based on search & category
  const filteredTopics = useMemo(() => {
    return filterHelpTopics(HELP_TOPICS, searchQuery, category);
  }, [searchQuery, category]);

  // Copy concise markdown guide
  const handleCopyGuide = async () => {
    const text = generateMarkdownGuide();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
    }
  };

  // Download markdown file
  const handleDownloadGuide = () => {
    const text = generateMarkdownGuide();
    const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fbmax-huong-dan-v${PRODUCT_METADATA.manifestVersion}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Scroll to anchor smoothly
  const scrollToAnchor = (anchorId: HelpSectionAnchor) => {
    const element = document.getElementById(anchorId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-brand-600 via-brand-700 to-slate-900 p-6 text-white shadow-card dark:border-slate-700/60 sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/90 backdrop-blur-sm">
              <CircleHelp className="h-3.5 w-3.5 text-brand-300" />
              Trung tâm trợ giúp & Tài liệu chính thức
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Hướng dẫn vận hành & Tiêu chuẩn FBMax
            </h1>
            <p className="max-w-2xl text-sm leading-relaxed text-brand-100">
              Tài liệu chuẩn kiến trúc Thin Client / Fat Server, hướng dẫn kết nối an toàn, minh bạch
              các khuôn mẫu đã xác thực và bảo vệ danh tính tài khoản Facebook của bạn.
            </p>
          </div>

          <div className="flex flex-wrap gap-2.5">
            <button
              onClick={() => setShowGuideModal(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/20"
            >
              <FileText className="h-4 w-4" />
              Xem cẩm nang
            </button>
            <button
              onClick={handleCopyGuide}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/20"
            >
              {copied ? <Check className="h-4 w-4 text-emerald-300" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Đã sao chép' : 'Sao chép Markdown'}
            </button>
            <button
              onClick={handleDownloadGuide}
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-xs font-bold text-slate-900 shadow-sm transition hover:bg-brand-50"
            >
              <Download className="h-4 w-4 text-brand-600" />
              Tải cẩm nang (.md)
            </button>
          </div>
        </div>
      </div>

      {/* Product & Environment Metadata Bar */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <TheSoLieu
          icon={<Radio className="h-5 w-5 text-brand-500" />}
          so={`v${PRODUCT_METADATA.manifestVersion}`}
          nhan={`Extension Companion (Tối thiểu v${PRODUCT_METADATA.minManifestVersion})`}
        />
        <TheSoLieu
          icon={<Layers className="h-5 w-5 text-indigo-500" />}
          so={`v${PRODUCT_METADATA.dashboardVersion}`}
          nhan="Phiên bản Dashboard"
        />
        <TheSoLieu
          icon={<Shield className="h-5 w-5 text-emerald-500" />}
          so={extConnected ? 'Đã ghép nối' : 'Chưa kết nối'}
          nhan={extVersion ? `Ext đang chạy: v${extVersion}` : 'Trạng thái kết nối máy của bạn'}
        />
        <TheSoLieu
          icon={<Lock className="h-5 w-5 text-amber-500" />}
          so={hasAccount ? 'Đã có Actor' : 'Chưa có Actor'}
          nhan="Tư cách Facebook hiện hành"
        />
      </div>

      {/* Quick Navigation Anchors */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3 dark:border-slate-700">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Chuyển nhanh:</span>
        <button
          onClick={() => scrollToAnchor('checklist')}
          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          Khởi đầu nhanh
        </button>
        <button
          onClick={() => scrollToAnchor('getting-started')}
          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          Cài đặt & Danh tính
        </button>
        <button
          onClick={() => scrollToAnchor('post-modes')}
          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          Chế độ đăng bài
        </button>
        <button
          onClick={() => scrollToAnchor('plans-payment')}
          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          Gói cước & VietQR
        </button>
        <button
          onClick={() => scrollToAnchor('troubleshooting')}
          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          Xử lý sự cố
        </button>
        <button
          onClick={() => scrollToAnchor('privacy-security')}
          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          Bảo mật phiên
        </button>
        <button
          onClick={() => scrollToAnchor('faq-support')}
          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          Job ID & Hỗ trợ
        </button>
      </div>

      {/* Interactive Quick-Start Checklist */}
      <section id="checklist">
        <The className="overflow-hidden">
          <DauThe
            icon={<CheckCircle2 className="h-4 w-4 text-brand-600" />}
            tieuDe="Checklist 5 bước khởi đầu nhanh"
            dem={`${completedCount}/${CHECKLIST_ITEMS.length} hoàn thành`}
            phai={
              <button
                onClick={resetChecklist}
                className="text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                Đặt lại
              </button>
            }
          />
          <div className="border-b border-slate-100 bg-amber-50/60 px-5 py-2.5 text-xs text-amber-800 dark:border-slate-700/60 dark:bg-amber-950/20 dark:text-amber-300">
            Checklist này lưu cục bộ tại trình duyệt (localStorage) để bạn tự theo dõi tiến độ cá nhân,
            không quyết định quyền hạn hay trạng thái trên máy chủ (DATA-01).
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
            {CHECKLIST_ITEMS.map((item) => {
              const isDone = Boolean(checklist[item.id]);
              return (
                <div
                  key={item.id}
                  className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between transition ${
                    isDone ? 'bg-slate-50/50 dark:bg-slate-800/40' : 'bg-white dark:bg-slate-800'
                  }`}
                >
                  <label className="flex cursor-pointer items-start gap-3 select-none">
                    <input
                      type="checkbox"
                      checked={isDone}
                      onChange={() => toggleChecklistItem(item.id)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-700"
                    />
                    <div className="space-y-0.5">
                      <p className={`text-sm font-semibold ${isDone ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>
                        {item.title}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
                    </div>
                  </label>

                  <div className="flex shrink-0 items-center gap-2 pl-7 sm:pl-0">
                    {item.actionRoute && (
                      <button
                        onClick={() => onDieuHuong(item.actionRoute!)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        {item.actionText || 'Mở màn'}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                    {item.actionExternal && (
                      <a
                        href={item.actionExternal}
                        target={item.actionExternal.startsWith('http') ? '_blank' : undefined}
                        rel={item.actionExternal.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 transition hover:bg-brand-100 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-300 dark:hover:bg-brand-500/20"
                      >
                        {item.actionText || 'Mở liên kết'}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </The>
      </section>

      {/* Search and Category Filter */}
      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Tìm kiếm chủ đề, lỗi, mã UNKNOWN, VietQR, cài đặt, Page..."
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-10 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {[
              { id: 'all', label: 'Tất cả' },
              { id: 'start', label: 'Bắt đầu' },
              { id: 'modes', label: 'Chế độ đăng' },
              { id: 'payment', label: 'Gói & VietQR' },
              { id: 'troubleshoot', label: 'Sự cố' },
              { id: 'security', label: 'Bảo mật' },
              { id: 'faq', label: 'FAQ' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setCategory(tab.id as HelpCategory)}
                className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${
                  category === tab.id
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {searchQuery && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Tìm thấy {filteredTopics.length} chủ đề phù hợp với từ khóa &ldquo;{searchQuery}&rdquo;
          </p>
        )}
      </div>

      {/* Topics Content Listing */}
      {filteredTopics.length === 0 ? (
        <The>
          <ChuaCoDuLieu
            chu="Không tìm thấy chủ đề trợ giúp nào phù hợp"
            goiY="Thử tìm bằng từ khóa khác như 'cài đặt', 'unknown', 'page', 'ảnh', 'vietqr' hoặc bấm Tất cả để xem toàn bộ danh mục."
          />
        </The>
      ) : (
        <div className="space-y-6">
          {filteredTopics.map((topic) => (
            <section key={topic.id} id={topic.anchor} className="scroll-mt-20">
              <The className="overflow-hidden">
                <DauThe
                  icon={<BookOpen className="h-4 w-4 text-brand-600" />}
                  tieuDe={topic.title}
                  phai={<NhanTrangThai chu={topic.badge} mau={topic.badgeColor} />}
                />
                <div className="space-y-4 p-5">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {topic.summary}
                  </p>

                  <div className="rounded-xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-600 dark:bg-slate-900/40 dark:text-slate-300">
                    <ul className="space-y-2">
                      {topic.details.map((line, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {topic.disclaimers && topic.disclaimers.length > 0 && (
                    <div className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-4 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
                      <div className="mb-2 flex items-center gap-1.5 font-bold text-amber-800 dark:text-amber-200">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        Lưu ý ranh giới kỹ thuật:
                      </div>
                      <ul className="space-y-1.5 pl-5 list-disc">
                        {topic.disclaimers.map((disc, idx) => (
                          <li key={idx}>{disc}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-between border-t border-slate-100 pt-3 dark:border-slate-700/60">
                    <span className="text-[11px] text-slate-400">
                      Mục: {topic.anchor} · fbmax-engine
                    </span>

                    {topic.actionRoute && (
                      <button
                        onClick={() => onDieuHuong(topic.actionRoute!)}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-600 transition hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
                      >
                        {topic.actionText || 'Chuyển tới màn hình'}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    )}

                    {topic.actionSpecial === 'upgrade' && (
                      <button
                        onClick={onMoNangCap}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-3 py-1.5 text-xs font-bold text-amber-950 transition hover:bg-amber-300"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        {topic.actionText || 'Mở bảng giá'}
                      </button>
                    )}

                    {topic.actionExternal && (
                      <a
                        href={topic.actionExternal}
                        target={topic.actionExternal.startsWith('http') ? '_blank' : undefined}
                        rel={topic.actionExternal.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-600 transition hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
                      >
                        {topic.actionText || 'Tải về / Truy cập'}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </The>
            </section>
          ))}
        </div>
      )}

      {/* Concise Guide Modal Dialog */}
      {showGuideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-slate-800">
            <div className="flex items-center justify-between border-b border-slate-100 p-5 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-brand-600" />
                <h3 className="font-bold text-slate-900 dark:text-white">Cẩm nang tóm tắt định dạng Markdown</h3>
              </div>
              <button
                onClick={() => setShowGuideModal(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              <pre className="whitespace-pre-wrap rounded-xl bg-slate-50 p-4 font-mono text-xs leading-relaxed text-slate-700 dark:bg-slate-900 dark:text-slate-300">
                {generateMarkdownGuide()}
              </pre>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-100 p-4 dark:border-slate-700">
              <button
                onClick={handleCopyGuide}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Đã sao chép' : 'Sao chép'}
              </button>
              <button
                onClick={handleDownloadGuide}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-xs font-bold text-white hover:bg-brand-700"
              >
                <Download className="h-3.5 w-3.5" />
                Tải file .md
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
